import { useState } from "react";
import {
  Card, Table, Button, Tag, Tabs, Switch, Modal, Form, Input, DatePicker,
  Popconfirm, message, Select, InputNumber,
} from "antd";
import { ArrowLeft, Plus, Trash2, Pencil } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCompanyUsers,
  useCompanyUserCreate,
  useCompanyUserUpdate,
  useCompanyUserDelete,
  useCompanyBranches,
  useCompanyBranchCreate,
  useCompanyBranchUpdate,
  useCompanyBranchDelete,
  useCompanyDivisions,
  useCompanyDivisionCreate,
  useCompanyDivisionUpdate,
  useCompanyDivisionDelete,
  useCompanyHolidays,
  useCompanyHolidayCreate,
  useCompanyHolidayUpdate,
  useCompanyHolidayDelete,
  useCompanyFeatureOverrides,
  useCompanyFeatureOverrideSet,
  useCompanyFeatureOverrideDelete,
  useCompanyUserSkills,
  useCompanyTeams,
  useCompanyTeamCreate,
  useCompanyTeamUpdate,
  useCompanyTeamDelete,
  useCompanyTeamMembers,
} from "../../../api-hooks/companyConfig";
import {
  TCompanyUser,
  TCompanyBranch,
  TCompanyDivision,
  TCompanyHoliday,
  TCompanyFeatureOverride,
  TUserSkill,
  TSkillProficiency,
  TTeam,
  TTeamMember,
} from "../../../types";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

const HOLIDAY_TYPE_OPTS = [
  { value: "bank_holiday", label: "Bank Holiday" },
  { value: "company_holiday", label: "Company Holiday" },
  { value: "public_holiday", label: "Public Holiday" },
  { value: "other", label: "Other" },
];

const EMP_TYPE_OPTS = [
  { value: "employee", label: "Employee" },
  { value: "contractor", label: "Contractor" },
  { value: "subcontractor", label: "Subcontractor" },
  { value: "casual", label: "Casual" },
];

export default function CompanyConfig() {
  const { companyId: companyIdParam } = useParams<{ companyId: string }>();
  const companyId = Number(companyIdParam);
  const navigate = useNavigate();
  const [messageApi, ctx] = message.useMessage();

  // Feature override form/modal
  const [addOverrideOpen, setAddOverrideOpen] = useState(false);
  const [overrideForm] = Form.useForm();

  // User modal
  const [userModal, setUserModal] = useState<{ open: boolean; rec: any | null }>({ open: false, rec: null });
  const [userForm] = Form.useForm();

  // Branch modal
  const [branchModal, setBranchModal] = useState<{ open: boolean; rec: any | null }>({ open: false, rec: null });
  const [branchForm] = Form.useForm();

  // Division modal
  const [divisionModal, setDivisionModal] = useState<{ open: boolean; rec: any | null }>({ open: false, rec: null });
  const [divisionForm] = Form.useForm();

  // Holiday modal
  const [holidayModal, setHolidayModal] = useState<{ open: boolean; rec: any | null }>({ open: false, rec: null });
  const [holidayForm] = Form.useForm();

  // Team modal
  const [teamModal, setTeamModal] = useState<{ open: boolean; rec: any | null }>({ open: false, rec: null });
  const [teamForm] = Form.useForm();
  const [expandedTeamId, setExpandedTeamId] = useState<number | null>(null);

  // ── Queries ──────────────────────────────────────────────────────────────
  const { data: users, isLoading: usersLoading } = useCompanyUsers(companyId);
  const { data: branches, isLoading: branchesLoading } = useCompanyBranches(companyId);
  const { data: divisions, isLoading: divisionsLoading } = useCompanyDivisions(companyId);
  const { data: holidays, isLoading: holidaysLoading } = useCompanyHolidays(companyId);
  const { data: overrides, isLoading: overridesLoading } = useCompanyFeatureOverrides(companyId);
  const { data: userSkills, isLoading: skillsLoading } = useCompanyUserSkills(companyId);
  const { data: teams, isLoading: teamsLoading } = useCompanyTeams(companyId);
  const { data: teamMembers, isLoading: membersLoading } = useCompanyTeamMembers(companyId, expandedTeamId ?? 0);

  // ── User mutations ────────────────────────────────────────────────────────
  const closeUserModal = () => { setUserModal({ open: false, rec: null }); userForm.resetFields(); };
  const { mutate: createUser, isPending: creatingUser } = useCompanyUserCreate(companyId, {
    onSuccess: () => { messageApi.success("User created."); closeUserModal(); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed to create user."),
  });
  const { mutate: updateUser, isPending: updatingUser } = useCompanyUserUpdate(companyId, {
    onSuccess: () => { messageApi.success("User updated."); closeUserModal(); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed to update user."),
  });
  const { mutate: deleteUser } = useCompanyUserDelete(companyId, {
    onSuccess: () => messageApi.success("User deleted."),
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed to delete user."),
  });

  // ── Feature Override mutations ────────────────────────────────────────────
  const { mutate: setOverride, isPending: isSettingOverride } = useCompanyFeatureOverrideSet(companyId, {
    onSuccess: () => { messageApi.success("Feature override saved."); setAddOverrideOpen(false); overrideForm.resetFields(); },
    onError: (err: any) => messageApi.error(err?.message || "Failed to save override."),
  });
  const { mutate: deleteOverride } = useCompanyFeatureOverrideDelete(companyId, {
    onSuccess: () => messageApi.success("Override removed."),
    onError: (err: any) => messageApi.error(err?.message || "Failed to remove override."),
  });

  // ── Branch mutations ──────────────────────────────────────────────────────
  const closeBranchModal = () => { setBranchModal({ open: false, rec: null }); branchForm.resetFields(); };
  const { mutate: createBranch, isPending: creatingBranch } = useCompanyBranchCreate(companyId, {
    onSuccess: () => { messageApi.success("Branch created."); closeBranchModal(); },
    onError: (e: any) => messageApi.error(e?.message || "Failed to create branch."),
  });
  const { mutate: updateBranch, isPending: updatingBranch } = useCompanyBranchUpdate(companyId, {
    onSuccess: () => { messageApi.success("Branch updated."); closeBranchModal(); },
    onError: (e: any) => messageApi.error(e?.message || "Failed to update branch."),
  });
  const { mutate: deleteBranch } = useCompanyBranchDelete(companyId, {
    onSuccess: () => messageApi.success("Branch deleted."),
    onError: (e: any) => messageApi.error(e?.message || "Failed to delete branch."),
  });

  // ── Division mutations ────────────────────────────────────────────────────
  const closeDivisionModal = () => { setDivisionModal({ open: false, rec: null }); divisionForm.resetFields(); };
  const { mutate: createDivision, isPending: creatingDivision } = useCompanyDivisionCreate(companyId, {
    onSuccess: () => { messageApi.success("Division created."); closeDivisionModal(); },
    onError: (e: any) => messageApi.error(e?.message || "Failed to create division."),
  });
  const { mutate: updateDivision, isPending: updatingDivision } = useCompanyDivisionUpdate(companyId, {
    onSuccess: () => { messageApi.success("Division updated."); closeDivisionModal(); },
    onError: (e: any) => messageApi.error(e?.message || "Failed to update division."),
  });
  const { mutate: deleteDivision } = useCompanyDivisionDelete(companyId, {
    onSuccess: () => messageApi.success("Division deleted."),
    onError: (e: any) => messageApi.error(e?.message || "Failed to delete division."),
  });

  // ── Holiday mutations ─────────────────────────────────────────────────────
  const closeHolidayModal = () => { setHolidayModal({ open: false, rec: null }); holidayForm.resetFields(); };
  const { mutate: createHoliday, isPending: creatingHoliday } = useCompanyHolidayCreate(companyId, {
    onSuccess: () => { messageApi.success("Holiday created."); closeHolidayModal(); },
    onError: (e: any) => messageApi.error(e?.message || "Failed to create holiday."),
  });
  const { mutate: updateHoliday, isPending: updatingHoliday } = useCompanyHolidayUpdate(companyId, {
    onSuccess: () => { messageApi.success("Holiday updated."); closeHolidayModal(); },
    onError: (e: any) => messageApi.error(e?.message || "Failed to update holiday."),
  });
  const { mutate: deleteHoliday } = useCompanyHolidayDelete(companyId, {
    onSuccess: () => messageApi.success("Holiday deleted."),
    onError: (e: any) => messageApi.error(e?.message || "Failed to delete holiday."),
  });

  // ── Team mutations ────────────────────────────────────────────────────────
  const closeTeamModal = () => { setTeamModal({ open: false, rec: null }); teamForm.resetFields(); };
  const { mutate: createTeam, isPending: creatingTeam } = useCompanyTeamCreate(companyId, {
    onSuccess: () => { messageApi.success("Team created."); closeTeamModal(); },
    onError: (e: any) => messageApi.error(e?.message || "Failed to create team."),
  });
  const { mutate: updateTeam, isPending: updatingTeam } = useCompanyTeamUpdate(companyId, {
    onSuccess: () => { messageApi.success("Team updated."); closeTeamModal(); },
    onError: (e: any) => messageApi.error(e?.message || "Failed to update team."),
  });
  const { mutate: deleteTeam } = useCompanyTeamDelete(companyId, {
    onSuccess: () => messageApi.success("Team deleted."),
    onError: (e: any) => messageApi.error(e?.message || "Failed to delete team."),
  });

  // ── Submit handlers ───────────────────────────────────────────────────────
  function handleUserSubmit(vals: any) {
    if (userModal.rec) updateUser({ id: userModal.rec.id, ...vals });
    else createUser(vals);
  }
  function handleBranchSubmit(vals: any) {
    if (branchModal.rec) updateBranch({ id: branchModal.rec.id, ...vals });
    else createBranch(vals);
  }
  function handleDivisionSubmit(vals: any) {
    if (divisionModal.rec) updateDivision({ id: divisionModal.rec.id, ...vals });
    else createDivision(vals);
  }
  function handleHolidaySubmit(vals: any) {
    const payload = { ...vals, date: vals.date ? dayjs(vals.date).format("YYYY-MM-DD") : undefined };
    if (holidayModal.rec) updateHoliday({ id: holidayModal.rec.id, ...payload });
    else createHoliday(payload);
  }
  function handleTeamSubmit(vals: any) {
    if (teamModal.rec) updateTeam({ id: teamModal.rec.id, ...vals });
    else createTeam(vals);
  }

  // ── Column helpers ────────────────────────────────────────────────────────
  const activeTag = (v: boolean) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>;

  // ── Column definitions ────────────────────────────────────────────────────
  const userColumns: ColumnsType<TCompanyUser> = [
    { title: "Name", dataIndex: "name", key: "name", render: (v) => <span className="text-sm text-slate-700">{v}</span> },
    { title: "Email", dataIndex: "email", key: "email", render: (v) => <span className="text-xs text-slate-500">{v || "—"}</span> },
    { title: "Role", dataIndex: "role_name", key: "role_name", render: (v) => v ? <Tag color="purple">{v}</Tag> : <span className="text-slate-300">—</span> },
    { title: "Employment", dataIndex: "employment_type", key: "employment_type", render: (v) => <span className="text-xs text-slate-500">{v || "—"}</span> },
    { title: "Status", dataIndex: "is_active", key: "is_active", render: (v) => activeTag(!!v) },
    { title: "Last Login", dataIndex: "last_login_at", key: "last_login_at", render: (v) => v ? <span className="text-xs text-slate-400">{new Date(v).toLocaleString("en-GB")}</span> : <span className="text-slate-300">—</span> },
    { title: "Joined", dataIndex: "created_at", key: "created_at", render: (v) => <span className="text-xs text-slate-400">{new Date(v).toLocaleDateString("en-GB")}</span> },
    {
      title: "", key: "actions", align: "right" as const,
      render: (_: any, r: TCompanyUser) => (
        <div className="flex gap-1 justify-end">
          <Button size="small" icon={<Pencil size={12} />} onClick={() => {
            setUserModal({ open: true, rec: r });
            userForm.setFieldsValue({
              first_name: r.first_name, last_name: r.last_name,
              email: r.email, phone: r.phone, mobile: r.mobile,
              job_title: r.job_title, employment_type: r.employment_type,
              status: r.status,
            });
          }}>Edit</Button>
          <Popconfirm title="Delete this user?" description="User will be deactivated and removed." onConfirm={() => deleteUser(r.id)} okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<Trash2 size={12} />}>Del</Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const branchColumns: ColumnsType<TCompanyBranch> = [
    { title: "Name", dataIndex: "name", key: "name", render: (v) => <span className="text-sm text-slate-700">{v}</span> },
    { title: "Code", dataIndex: "code", key: "code", render: (v) => <span className="font-mono text-xs text-slate-500">{v || "—"}</span> },
    { title: "Phone", dataIndex: "phone", key: "phone", render: (v) => <span className="text-xs text-slate-500">{v || "—"}</span> },
    { title: "Email", dataIndex: "email", key: "email", render: (v) => <span className="text-xs text-slate-500">{v || "—"}</span> },
    { title: "Address", dataIndex: "address", key: "address", render: (v) => <span className="text-xs text-slate-500">{v || "—"}</span> },
    { title: "Active", dataIndex: "is_active", key: "is_active", render: (v) => activeTag(v) },
    {
      title: "", key: "actions", align: "right" as const,
      render: (_: any, r: TCompanyBranch) => (
        <div className="flex gap-1 justify-end">
          <Button size="small" icon={<Pencil size={12} />} onClick={() => {
            setBranchModal({ open: true, rec: r });
            branchForm.setFieldsValue({ name: r.name, code: r.code, phone: r.phone, email: r.email, address: r.address, notes: r.notes, is_active: !!r.is_active });
          }}>Edit</Button>
          <Popconfirm title="Delete this branch?" onConfirm={() => deleteBranch(r.id)} okButtonProps={{ danger: true }}><Button size="small" danger icon={<Trash2 size={12} />}>Del</Button></Popconfirm>
        </div>
      ),
    },
  ];

  const divisionColumns: ColumnsType<TCompanyDivision> = [
    { title: "Name", dataIndex: "name", key: "name", render: (v) => <span className="text-sm text-slate-700">{v}</span> },
    { title: "Code", dataIndex: "code", key: "code", render: (v) => <span className="font-mono text-xs text-slate-500">{v || "—"}</span> },
    { title: "Branch", dataIndex: "branch_name", key: "branch_name", render: (v) => v ? <Tag color="blue">{v}</Tag> : <span className="text-slate-300">—</span> },
    { title: "Description", dataIndex: "description", key: "description", render: (v) => <span className="text-xs text-slate-500">{v || "—"}</span> },
    { title: "Active", dataIndex: "is_active", key: "is_active", render: (v) => activeTag(v) },
    {
      title: "", key: "actions", align: "right" as const,
      render: (_: any, r: TCompanyDivision) => (
        <div className="flex gap-1 justify-end">
          <Button size="small" icon={<Pencil size={12} />} onClick={() => {
            setDivisionModal({ open: true, rec: r });
            divisionForm.setFieldsValue({ name: r.name, code: r.code, description: r.description, branch_id: r.branch_id, is_active: !!r.is_active });
          }}>Edit</Button>
          <Popconfirm title="Delete this division?" onConfirm={() => deleteDivision(r.id)} okButtonProps={{ danger: true }}><Button size="small" danger icon={<Trash2 size={12} />}>Del</Button></Popconfirm>
        </div>
      ),
    },
  ];

  const holidayColumns: ColumnsType<TCompanyHoliday> = [
    { title: "Name", dataIndex: "name", key: "name", render: (v) => <span className="text-sm text-slate-700">{v}</span> },
    { title: "Date", dataIndex: "date", key: "date", render: (v) => <span className="text-xs text-slate-500">{new Date(v).toLocaleDateString("en-GB")}</span> },
    { title: "Type", dataIndex: "holiday_type", key: "holiday_type", render: (v) => <Tag color="blue">{v}</Tag> },
    { title: "Recurring", dataIndex: "is_recurring", key: "is_recurring", render: (v) => <Tag color={v ? "cyan" : "default"}>{v ? "Yes" : "No"}</Tag> },
    { title: "Active", dataIndex: "is_active", key: "is_active", render: (v) => activeTag(v) },
    {
      title: "", key: "actions", align: "right" as const,
      render: (_: any, r: TCompanyHoliday) => (
        <div className="flex gap-1 justify-end">
          <Button size="small" icon={<Pencil size={12} />} onClick={() => {
            setHolidayModal({ open: true, rec: r });
            holidayForm.setFieldsValue({ name: r.name, date: r.date ? dayjs(r.date) : null, holiday_type: r.holiday_type, is_recurring: !!r.is_recurring, is_active: !!r.is_active });
          }}>Edit</Button>
          <Popconfirm title="Delete this holiday?" onConfirm={() => deleteHoliday(r.id)} okButtonProps={{ danger: true }}><Button size="small" danger icon={<Trash2 size={12} />}>Del</Button></Popconfirm>
        </div>
      ),
    },
  ];

  const proficiencyColor: Record<TSkillProficiency, string> = { basic: "default", intermediate: "blue", advanced: "orange", expert: "green" };
  const skillColumns: ColumnsType<TUserSkill> = [
    { title: "User", dataIndex: "user_name", key: "user_name", render: (v) => <span className="text-sm text-slate-700">{v}</span> },
    { title: "Email", dataIndex: "user_email", key: "user_email", render: (v) => <span className="text-xs text-slate-500">{v}</span> },
    { title: "Skill", dataIndex: "skill_name", key: "skill_name", render: (v) => <span className="text-sm font-medium text-slate-700">{v}</span> },
    { title: "Proficiency", dataIndex: "proficiency", key: "proficiency", render: (v: TSkillProficiency) => <Tag color={proficiencyColor[v]}>{v}</Tag> },
    { title: "Verified By", dataIndex: "verified_by_name", key: "verified_by_name", render: (v) => <span className="text-xs text-slate-500">{v || "—"}</span> },
    { title: "Active", dataIndex: "is_active", key: "is_active", render: (v) => activeTag(v) },
  ];

  const teamColumns: ColumnsType<TTeam> = [
    { title: "Name", dataIndex: "name", key: "name", render: (v) => <span className="text-sm font-medium text-slate-700">{v}</span> },
    { title: "Description", dataIndex: "description", key: "description", render: (v) => <span className="text-xs text-slate-500">{v || "—"}</span> },
    { title: "Lead", dataIndex: "team_lead_name", key: "team_lead_name", render: (v) => v?.trim() ? <Tag color="purple">{v}</Tag> : <span className="text-slate-300">—</span> },
    { title: "Branch", dataIndex: "branch_name", key: "branch_name", render: (v) => <span className="text-xs text-slate-500">{v || "—"}</span> },
    { title: "Active", dataIndex: "is_active", key: "is_active", render: (v) => activeTag(v) },
    {
      title: "", key: "actions", align: "right" as const,
      render: (_: any, r: TTeam) => (
        <div className="flex gap-1 justify-end">
          <Button size="small" type={expandedTeamId === r.id ? "primary" : "default"} onClick={() => setExpandedTeamId(expandedTeamId === r.id ? null : r.id)}>Members</Button>
          <Button size="small" icon={<Pencil size={12} />} onClick={() => {
            setTeamModal({ open: true, rec: r });
            teamForm.setFieldsValue({ name: r.name, description: r.description, team_lead_id: r.team_lead_id, branch_id: r.branch_id, is_active: !!r.is_active });
          }}>Edit</Button>
          <Popconfirm title="Delete this team?" onConfirm={() => deleteTeam(r.id)} okButtonProps={{ danger: true }}><Button size="small" danger icon={<Trash2 size={12} />}>Del</Button></Popconfirm>
        </div>
      ),
    },
  ];

  const memberColumns: ColumnsType<TTeamMember> = [
    { title: "Name", dataIndex: "user_name", key: "user_name", render: (v) => <span className="text-sm text-slate-700">{v}</span> },
    { title: "Email", dataIndex: "user_email", key: "user_email", render: (v) => <span className="text-xs text-slate-500">{v}</span> },
    { title: "Role", dataIndex: "role_name", key: "role_name", render: (v) => v ? <Tag color="blue">{v}</Tag> : <span className="text-slate-300">—</span> },
    { title: "Joined", dataIndex: "joined_at", key: "joined_at", render: (v) => <span className="text-xs text-slate-400">{new Date(v).toLocaleDateString("en-GB")}</span> },
  ];

  const overrideColumns: ColumnsType<TCompanyFeatureOverride> = [
    { title: "Feature Key", dataIndex: "feature_key", key: "feature_key", render: (v) => <span className="font-mono text-xs text-slate-700">{v}</span> },
    { title: "Enabled", dataIndex: "is_enabled", key: "is_enabled", render: (v) => <Switch checked={v} disabled size="small" /> },
    { title: "Value", dataIndex: "value", key: "value", render: (v) => <span className="font-mono text-xs text-slate-500">{v || "—"}</span> },
    { title: "Reason", dataIndex: "override_reason", key: "override_reason", render: (v) => <span className="text-xs text-slate-500">{v || "—"}</span> },
    { title: "Expires", dataIndex: "expires_at", key: "expires_at", render: (v) => v ? <span className="text-xs text-slate-400">{new Date(v).toLocaleDateString("en-GB")}</span> : <span className="text-slate-300">—</span> },
    { title: "Granted By", dataIndex: "granted_by_name", key: "granted_by_name", render: (v) => <span className="text-xs text-slate-500">{v || "—"}</span> },
    {
      title: "Actions", key: "actions", align: "right" as const,
      render: (_: any, r: TCompanyFeatureOverride) => (
        <Popconfirm title="Remove this override?" description="Company reverts to plan defaults." onConfirm={() => deleteOverride(r.feature_key)} okText="Remove" okButtonProps={{ danger: true }}>
          <Button size="small" danger icon={<Trash2 size={12} />}>Remove</Button>
        </Popconfirm>
      ),
    },
  ];

  // ── Tab header with Add button ────────────────────────────────────────────
  function tabHeaderWithAdd(label: string, onAdd: () => void) {
    return (
      <div className="flex items-center justify-between mb-3">
        <span />
        <Button size="small" icon={<Plus size={13} />} style={{ background: "#4c1d95", borderColor: "#4c1d95", color: "#fff" }} onClick={onAdd}>Add {label}</Button>
      </div>
    );
  }

  const branchOpts = (branches ?? []).map((b: any) => ({ value: b.id, label: b.name }));

  // ── Tabs ──────────────────────────────────────────────────────────────────
  const tabItems = [
    {
      key: "users",
      label: "Users",
      children: (
        <div>
          {tabHeaderWithAdd("User", () => { setUserModal({ open: true, rec: null }); userForm.resetFields(); userForm.setFieldsValue({ employment_type: "employee", status: "ACTIVE" }); })}
          <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
            <Table dataSource={users ?? []} columns={userColumns} rowKey="id" pagination={false} loading={usersLoading} size="middle" scroll={{ x: 1000 }} locale={{ emptyText: "No users found." }} />
          </Card>
        </div>
      ),
    },
    {
      key: "branches",
      label: "Branches",
      children: (
        <div>
          {tabHeaderWithAdd("Branch", () => { setBranchModal({ open: true, rec: null }); branchForm.resetFields(); branchForm.setFieldsValue({ is_active: true }); })}
          <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
            <Table dataSource={branches ?? []} columns={branchColumns} rowKey="id" pagination={false} loading={branchesLoading} size="middle" scroll={{ x: 800 }} locale={{ emptyText: "No branches found." }} />
          </Card>
        </div>
      ),
    },
    {
      key: "divisions",
      label: "Divisions",
      children: (
        <div>
          {tabHeaderWithAdd("Division", () => { setDivisionModal({ open: true, rec: null }); divisionForm.resetFields(); divisionForm.setFieldsValue({ is_active: true }); })}
          <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
            <Table dataSource={divisions ?? []} columns={divisionColumns} rowKey="id" pagination={false} loading={divisionsLoading} size="middle" scroll={{ x: 800 }} locale={{ emptyText: "No divisions found." }} />
          </Card>
        </div>
      ),
    },
    {
      key: "holidays",
      label: "Holidays",
      children: (
        <div>
          {tabHeaderWithAdd("Holiday", () => { setHolidayModal({ open: true, rec: null }); holidayForm.resetFields(); holidayForm.setFieldsValue({ is_active: true, is_recurring: false, holiday_type: "company_holiday" }); })}
          <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
            <Table dataSource={holidays ?? []} columns={holidayColumns} rowKey="id" pagination={false} loading={holidaysLoading} size="middle" scroll={{ x: 700 }} locale={{ emptyText: "No holidays found." }} />
          </Card>
        </div>
      ),
    },
    {
      key: "user-skills",
      label: "User Skills",
      children: (
        <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
          <Table dataSource={userSkills ?? []} columns={skillColumns} rowKey="id" pagination={false} loading={skillsLoading} size="middle" scroll={{ x: 900 }} locale={{ emptyText: "No skills recorded." }} />
        </Card>
      ),
    },
    {
      key: "teams",
      label: "Teams",
      children: (
        <div>
          {tabHeaderWithAdd("Team", () => { setTeamModal({ open: true, rec: null }); teamForm.resetFields(); teamForm.setFieldsValue({ is_active: true }); })}
          <Card className="rounded-2xl border border-slate-200 shadow-sm mb-3" styles={{ body: { padding: 0 } }}>
            <Table dataSource={teams ?? []} columns={teamColumns} rowKey="id" pagination={false} loading={teamsLoading} size="middle" scroll={{ x: 800 }} locale={{ emptyText: "No teams found." }} />
          </Card>
          {expandedTeamId && (
            <Card className="rounded-2xl border border-purple-200 shadow-sm" styles={{ body: { padding: 0 } }} title={<span className="text-sm font-medium text-slate-700">Members — {teams?.find(t => t.id === expandedTeamId)?.name}</span>}>
              <Table dataSource={teamMembers ?? []} columns={memberColumns} rowKey="id" pagination={false} loading={membersLoading} size="small" scroll={{ x: 600 }} locale={{ emptyText: "No members in this team." }} />
            </Card>
          )}
        </div>
      ),
    },
    {
      key: "feature-overrides",
      label: "Feature Overrides",
      children: (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-slate-500">Grant/revoke specific features for this company outside their subscription plan.</p>
            <Button icon={<Plus size={14} />} style={{ background: "#4c1d95", borderColor: "#4c1d95", color: "#fff" }} onClick={() => { setAddOverrideOpen(true); overrideForm.resetFields(); }}>Add Override</Button>
          </div>
          <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
            <Table dataSource={overrides ?? []} columns={overrideColumns} rowKey="feature_key" pagination={false} loading={overridesLoading} size="middle" scroll={{ x: 900 }} locale={{ emptyText: "No feature overrides configured." }} />
          </Card>
        </div>
      ),
    },
  ];

  return (
    <div>
      {ctx}

      <div className="flex items-center gap-3 mb-6">
        <Button icon={<ArrowLeft size={14} />} onClick={() => navigate("/companies")}>Back to Companies</Button>
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Company Config</h2>
          <p className="text-slate-500 text-sm mt-0.5">Company ID: {companyId}</p>
        </div>
      </div>

      <Tabs defaultActiveKey="users" items={tabItems} />

      {/* ── User Modal ── */}
      <Modal open={userModal.open} onCancel={closeUserModal} title={userModal.rec ? "Edit User" : "Add User"} footer={null} destroyOnClose width={560}>
        <Form form={userForm} layout="vertical" onFinish={handleUserSubmit} className="mt-4" autoComplete="off">
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="first_name" label="First Name" rules={[{ required: true, message: "Required" }]}><Input autoComplete="off" /></Form.Item>
            <Form.Item name="last_name" label="Last Name"><Input autoComplete="off" /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="email" label="Email" rules={[{ type: "email" }]}><Input autoComplete="new-email" /></Form.Item>
            <Form.Item name="phone" label="Phone"><Input autoComplete="off" /></Form.Item>
          </div>
          {!userModal.rec && (
            <Form.Item name="password" label="Password" rules={[{ required: true, min: 6, message: "Min 6 characters" }]}>
              <Input.Password placeholder="Min 6 characters" autoComplete="new-password" />
            </Form.Item>
          )}
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="job_title" label="Job Title"><Input autoComplete="off" /></Form.Item>
            <Form.Item name="employment_type" label="Employment Type"><Select options={EMP_TYPE_OPTS} /></Form.Item>
          </div>
          {userModal.rec && (
            <Form.Item name="status" label="Status">
              <Select options={[{ value: "ACTIVE", label: "Active" }, { value: "INACTIVE", label: "Inactive" }]} />
            </Form.Item>
          )}
          <div className="flex gap-2 justify-end">
            <Button onClick={closeUserModal}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={creatingUser || updatingUser} style={{ background: "#4c1d95", borderColor: "#4c1d95" }}>
              {userModal.rec ? "Save" : "Create User"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ── Branch Modal ── */}
      <Modal open={branchModal.open} onCancel={closeBranchModal} title={branchModal.rec ? "Edit Branch" : "Add Branch"} footer={null} destroyOnClose>
        <Form form={branchForm} layout="vertical" onFinish={handleBranchSubmit} className="mt-4">
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Name required" }]}><Input /></Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="code" label="Code"><Input placeholder="e.g. LON" /></Form.Item>
            <Form.Item name="phone" label="Phone"><Input /></Form.Item>
          </div>
          <Form.Item name="email" label="Email"><Input type="email" /></Form.Item>
          <Form.Item name="address" label="Address"><Input.TextArea rows={2} placeholder="Full address" /></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked"><Switch /></Form.Item>
          <div className="flex gap-2 justify-end">
            <Button onClick={closeBranchModal}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={creatingBranch || updatingBranch} style={{ background: "#4c1d95", borderColor: "#4c1d95" }}>
              {branchModal.rec ? "Save" : "Create"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ── Division Modal ── */}
      <Modal open={divisionModal.open} onCancel={closeDivisionModal} title={divisionModal.rec ? "Edit Division" : "Add Division"} footer={null} destroyOnClose>
        <Form form={divisionForm} layout="vertical" onFinish={handleDivisionSubmit} className="mt-4">
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Name required" }]}><Input /></Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="code" label="Code"><Input placeholder="e.g. DIV-01" /></Form.Item>
            <Form.Item name="is_active" label="Active" valuePropName="checked"><Switch /></Form.Item>
          </div>
          <Form.Item name="branch_id" label="Branch (optional)">
            <Select allowClear placeholder="Select branch…" options={branchOpts} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex gap-2 justify-end">
            <Button onClick={closeDivisionModal}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={creatingDivision || updatingDivision} style={{ background: "#4c1d95", borderColor: "#4c1d95" }}>
              {divisionModal.rec ? "Save" : "Create"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ── Holiday Modal ── */}
      <Modal open={holidayModal.open} onCancel={closeHolidayModal} title={holidayModal.rec ? "Edit Holiday" : "Add Holiday"} footer={null} destroyOnClose>
        <Form form={holidayForm} layout="vertical" onFinish={handleHolidaySubmit} className="mt-4">
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Name required" }]}><Input /></Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="date" label="Date" rules={[{ required: true, message: "Date required" }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="holiday_type" label="Type"><Select options={HOLIDAY_TYPE_OPTS} /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="is_recurring" label="Recurring" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="is_active" label="Active" valuePropName="checked"><Switch /></Form.Item>
          </div>
          <div className="flex gap-2 justify-end">
            <Button onClick={closeHolidayModal}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={creatingHoliday || updatingHoliday} style={{ background: "#4c1d95", borderColor: "#4c1d95" }}>
              {holidayModal.rec ? "Save" : "Create"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ── Team Modal ── */}
      <Modal open={teamModal.open} onCancel={closeTeamModal} title={teamModal.rec ? "Edit Team" : "Add Team"} footer={null} destroyOnClose>
        <Form form={teamForm} layout="vertical" onFinish={handleTeamSubmit} className="mt-4">
          <Form.Item name="name" label="Team Name" rules={[{ required: true, message: "Name required" }]}><Input /></Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="team_lead_id" label="Team Lead">
              <Select showSearch allowClear placeholder="Select user" optionFilterProp="label"
                options={(users ?? []).map((u: any) => ({ value: u.id, label: `${u.name || u.email}` }))} />
            </Form.Item>
            <Form.Item name="branch_id" label="Branch">
              <Select showSearch allowClear placeholder="Select branch" optionFilterProp="label"
                options={branchOpts} />
            </Form.Item>
          </div>
          <Form.Item name="is_active" label="Active" valuePropName="checked"><Switch /></Form.Item>
          <div className="flex gap-2 justify-end">
            <Button onClick={closeTeamModal}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={creatingTeam || updatingTeam} style={{ background: "#4c1d95", borderColor: "#4c1d95" }}>
              {teamModal.rec ? "Save" : "Create"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ── Feature Override Modal ── */}
      <Modal title="Add Feature Override" open={addOverrideOpen} onOk={() => overrideForm.validateFields().then(vals => setOverride({ feature_key: vals.feature_key, is_enabled: vals.is_enabled ?? false, value: vals.value || undefined, override_reason: vals.override_reason || undefined, expires_at: vals.expires_at ? dayjs(vals.expires_at).toISOString() : undefined }))} onCancel={() => { setAddOverrideOpen(false); overrideForm.resetFields(); }} okText="Save Override" confirmLoading={isSettingOverride} okButtonProps={{ style: { background: "#4c1d95", borderColor: "#4c1d95" } }}>
        <Form form={overrideForm} layout="vertical" className="mt-4" initialValues={{ is_enabled: true }}>
          <Form.Item name="feature_key" label="Feature Key" rules={[{ required: true, message: "Feature key is required" }]}><Input placeholder="e.g. advanced_reporting" /></Form.Item>
          <Form.Item name="is_enabled" label="Enabled" valuePropName="checked"><Switch /></Form.Item>
          <Form.Item name="value" label="Value (JSON or string)"><Input.TextArea rows={3} placeholder='e.g. { "limit": 100 }' /></Form.Item>
          <Form.Item name="override_reason" label="Override Reason"><Input placeholder="Why is this override being granted?" /></Form.Item>
          <Form.Item name="expires_at" label="Expires At (optional)"><DatePicker style={{ width: "100%" }} showTime /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
